import React from "react";
import { Link } from "react-router-dom";
import {
  Reveal,
  MaskLine,
  SectionMark,
  GridLines,
  RegMarks,
  Glass,
  Shell,
} from "../Components/Primitives";
import StackIndex, { STACK_COUNT } from "../Components/StackIndex";
import PracticeCard from "../Components/PracticeCards";
import Contact from "../Components/Contact";
import Seo from "../Components/Seo";
import Footer from "./Footer";
import { featured, inProgress } from "../data/projects";
import profile from "../Assets/teemi.avif";

const SEO = {
  title: "Timi Gbenga - Front-end & Mobile Engineer in Lagos, Nigeria",
  description:
    "Timi Gbenga is a front-end and mobile engineer in Lagos, Nigeria with 3+ years building production React, Next.js and React Native apps. Creator of myQuest, a CBT exam prep app with 1,000+ downloads on Google Play.",
  path: "/",
};

/* The drawing's title block - the metadata strip that runs
   under the hero, the way a drafting sheet carries its specs. */
const TITLE_BLOCK = [
  { k: "Discipline", v: "Frontend / Mobile" },
  { k: "Experience", v: "3+ Years" },
  { k: "Based", v: "Lagos, NG" },
  { k: "Status", v: "Available", live: true },
];

const PRACTICE = [
  {
    variant: "assemble",
    tag: "Interface",
    title: "Interface Engineering",
    body: "Production React and Next.js front-ends that are typed, accessible and fast on the devices people actually own. Design systems that hold up past the first sprint.",
    tags: ["React", "Next.js", "TypeScript"],
  },
  {
    variant: "reflow",
    tag: "Apps",
    title: "Mobile & Desktop",
    body: "Cross-platform apps with React Native and Expo, and native desktop with Tauri. First screen through store submission, and the releases that follow.",
    tags: ["React Native", "Expo", "Tauri"],
  },
  {
    variant: "tilt",
    tag: "Motion",
    title: "Motion & 3D",
    body: "GSAP scroll systems, parallax and Three.js scenes, used where they carry meaning rather than bolted on as decoration at the end.",
    tags: ["GSAP", "Three.js"],
  },
  {
    variant: "glitch",
    tag: "Intelligence",
    title: "Applied AI",
    body: "Python and PyTorch work that ends in a product decision: assistants, verification systems, and models whose output something can act on.",
    tags: ["Python", "PyTorch", "SHAP"],
  },
];

/* ---------------------------------------------------------
   Project row - the work as an index entry, not a card.
   --------------------------------------------------------- */
const ProjectRow = ({ project, delay }) => (
  <Reveal delay={delay} className="border-b border-rule">
    <a
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block py-8 md:py-11"
    >
      <span className="absolute -inset-x-4 md:-inset-x-8 inset-y-0 -z-10 rounded-2xl bg-ink/[0.03] opacity-0 transition-opacity duration-700 ease-glass group-hover:opacity-100" />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_auto] md:items-center md:gap-12">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
            <h3 className="display display-sm transition-transform duration-700 ease-glass md:group-hover:translate-x-2">
              {project.title}
            </h3>
            <span className="label">{project.discipline}</span>
          </div>

          <p className="lede max-w-xl pt-4 text-[15px] md:text-[16px]">
            {project.summary}
          </p>

          {/* Hard numbers, set as a drafting sub-block */}
          {project.metrics && (
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 pt-5">
              {project.metrics.map((m) => (
                <span key={m.k} className="flex items-baseline gap-2">
                  <span className="text-[17px] font-medium tracking-[-0.025em] text-ink tabular-nums">
                    {m.v}
                  </span>
                  <span className="label">{m.k}</span>
                </span>
              ))}
            </div>
          )}

          <ul className="flex flex-wrap gap-2 pt-5">
            {project.stack.map((s) => (
              <li
                key={s}
                className="mono rounded-full border border-rule-2 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-ink-70"
              >
                {s}
              </li>
            ))}
          </ul>
        </div>

        {/* Preview plate - present at rest, lifts on hover */}
        <div className="md:w-[248px] lg:w-[300px]">
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-rule bg-ground-sunk">
            <img
              src={project.image}
              alt={`${project.title} interface`}
              loading="lazy"
              className="h-full w-full object-cover object-top transition-transform duration-[1.2s] ease-glass md:group-hover:scale-[1.04]"
            />
          </div>
          <div className="flex items-center justify-between pt-3">
            <span className="label">{project.year}</span>
            <span className="label flex items-center gap-1.5 text-ink">
              View
              <span className="transition-transform duration-500 ease-glass group-hover:translate-x-1">
                &rarr;
              </span>
            </span>
          </div>
        </div>
      </div>
    </a>
  </Reveal>
);

const Homepage = () => {
  return (
    <main className="relative">
      <Seo {...SEO} />
      <GridLines count={5} />

      <div className="relative z-10">
        {/* ============ 01 INDEX ============ */}
        <section id="hero" className="pt-32 md:pt-44 pb-16 md:pb-24">
          <Shell>
            <Reveal className="flex items-center gap-4 pb-10 md:pb-16">
              <span className="h-px w-10 bg-[var(--rule-2)]" />
              <span className="label">Index</span>
            </Reveal>

            <div className="grid gap-14 lg:grid-cols-[1.35fr_0.65fr] lg:gap-16 lg:items-end">
              {/* Name, set as architecture */}
              <div>
                <h1 className="display display-lg">
                  <MaskLine delay={60}>Timi</MaskLine>
                  <MaskLine delay={160}>
                    <span className="text-ink-45">Gbenga</span>
                  </MaskLine>
                </h1>

                <Reveal delay={340} className="pt-8 md:pt-12">
                  <p className="lede max-w-xl">
                    Software Engineer, 3+ years shipping production web and
                    mobile apps. I build considered, high-performance products,
                    the kind where the structure is visible and nothing is there
                    by accident.
                  </p>
                </Reveal>

                <Reveal
                  delay={420}
                  className="flex flex-wrap items-center gap-3 pt-10"
                >
                  <Link
                    to="/projects"
                    className="group inline-flex items-center gap-3 rounded-full bg-ink px-7 py-3.5 text-[13.5px] font-medium text-ground-raise transition-opacity duration-500 hover:opacity-85"
                  >
                    Selected work
                    <span className="transition-transform duration-500 ease-glass group-hover:translate-x-1">
                      &rarr;
                    </span>
                  </Link>
                  <a
                    href="#contact"
                    className="inline-flex items-center rounded-full border border-rule-2 px-7 py-3.5 text-[13.5px] font-medium text-ink transition-colors duration-500 hover:border-ink"
                  >
                    Start a project
                  </a>
                </Reveal>
              </div>

              {/* Portrait, framed in glass */}
              <Reveal delay={240}>
                <Glass className="relative rounded-[26px] p-3">
                  <RegMarks inset={10} />
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[18px] bg-ground-sunk">
                    <img
                      src={profile}
                      alt="Timi Gbenga"
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex items-baseline justify-between px-2 pb-1 pt-3.5">
                    <span className="label">Portrait</span>
                    <span className="label">Lagos</span>
                  </div>
                </Glass>
              </Reveal>
            </div>
          </Shell>
        </section>

        {/* Title block */}
        <Shell>
          <Reveal className="grid grid-cols-2 border-y border-rule md:grid-cols-4">
            {TITLE_BLOCK.map((item, i) => (
              <div
                key={item.k}
                className={[
                  "px-1 py-6 md:px-6 md:py-8",
                  i % 2 === 1 ? "border-l border-rule" : "",
                  i >= 2 ? "border-t border-rule md:border-t-0" : "",
                  i === 2 || i === 3 ? "md:border-l md:border-rule" : "",
                ].join(" ")}
              >
                <p className="label pb-2.5">{item.k}</p>
                <p className="flex items-center gap-2 text-[14px] font-medium tracking-[-0.015em] text-ink">
                  {item.live && (
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-70" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                    </span>
                  )}
                  {item.v}
                </p>
              </div>
            ))}
          </Reveal>
        </Shell>

        {/* ============ 02 WORK ============ */}
        <section id="work" className="py-20 md:py-28">
          <Shell>
            <SectionMark title="Selected Work" meta="2024 to 2025" />

            <Reveal className="max-w-2xl pb-10 pt-8 md:pb-14">
              <h2 className="display display-md">
                Things I have
                <br />
                <span className="text-ink-45">put into the world.</span>
              </h2>
            </Reveal>

            <div className="border-t border-rule">
              {featured.map((p, i) => (
                <ProjectRow key={p.id} project={p} delay={i * 90} />
              ))}
            </div>

            <Reveal delay={140} className="pt-10">
              <Link
                to="/projects"
                className="group inline-flex items-center gap-3 text-[15px] font-medium tracking-[-0.015em] text-ink"
              >
                <span className="link-draw">View the full index</span>
                <span className="transition-transform duration-500 ease-glass group-hover:translate-x-1.5">
                  &rarr;
                </span>
              </Link>
            </Reveal>
          </Shell>
        </section>

        {/* ============ IN PROGRESS ============ */}
        <section className="py-16 md:py-20">
          <Shell>
            <SectionMark
              title="Currently Building"
              meta={`${inProgress.length} in progress`}
            />

            <div
              className={`grid gap-5 pt-8 ${
                inProgress.length > 1 ? "md:grid-cols-2" : ""
              }`}
            >
              {inProgress.map((item, i) => (
                <Reveal key={item.title} delay={i * 90}>
                  <div className="relative h-full rounded-[22px] border border-dashed border-rule-2 p-7 md:p-8">
                    <div className="flex items-baseline justify-between gap-4 pb-6">
                      <span className="label flex items-center gap-2 text-ink">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-70" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                        </span>
                        In progress
                      </span>
                      <span className="label">{item.year}</span>
                    </div>

                    <h3 className="text-[24px] font-medium tracking-[-0.03em] leading-[1.1]">
                      {item.title}
                    </h3>
                    <p className="label pt-2">{item.discipline}</p>
                    <p className="max-w-xl pt-4 text-[15px] leading-[1.6] tracking-[-0.01em] text-ink-70">
                      {item.summary}
                    </p>

                    <ul className="flex flex-wrap gap-2 pt-6">
                      {item.stack.map((s) => (
                        <li
                          key={s}
                          className="mono rounded-full border border-rule-2 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-ink-70"
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </Shell>
        </section>

        {/* ============ 03 PRACTICE ============ */}
        <section id="practice" className="py-20 md:py-28">
          <Shell>
            <SectionMark title="Practice" meta="Four disciplines" />

            <Reveal className="max-w-2xl pb-12 pt-8 md:pb-16">
              <h2 className="display display-md">
                What I do,
                <br />
                <span className="text-ink-45">and how.</span>
              </h2>
            </Reveal>

            <div className="grid gap-5 md:grid-cols-2">
              {PRACTICE.map((item) => (
                <PracticeCard key={item.title} {...item} />
              ))}
            </div>
          </Shell>
        </section>

        {/* ============ 04 STACK ============ */}
        <section id="stack" className="py-20 md:py-28">
          <Shell>
            <SectionMark title="Stack" meta={`${STACK_COUNT} specimens`} />

            <Reveal className="max-w-2xl pb-12 pt-8 md:pb-14">
              <h2 className="display display-md">
                Tools, listed
                <br />
                <span className="text-ink-45">without ceremony.</span>
              </h2>
            </Reveal>

            <StackIndex />
          </Shell>
        </section>

        {/* ============ 05 CONTACT ============ */}
        <Contact />

        <Footer />
      </div>
    </main>
  );
};

export default Homepage;
